import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { createElementFromHTML } from 'shared/lib/createElementFromHTML'
import { logger } from 'shared/lib'
import { getCaretIndex, placeCaretAtEnd, placeCaretAtStart, textToHtml } from 'widgets/MessageField'
import { useMergeFields } from 'widgets/MergeField'
import { useTextToSpeechContext } from 'widgets/TextToSpeech/context/context'
import styles from './styles.module.scss'

export const TextToSpeechText = observer(({ className }: { className?: string }) => {
  const { textToSpeechTextStore } = useTextToSpeechContext()
  const {
    setAddContentFn,
    messageHtml,
    messageInnerText,
    setMessageText,
    clearError,
    replaceTriggerContent,
    updateTriggerContent,
    setUpdateTriggerContent,
    entities,
    focusMessageFieldTrigger,
    setActiveMessageField,
    activeMessageField,
    type,
  } = textToSpeechTextStore
  const ref = useRef<HTMLDivElement>(null)
  const offsetRef = useRef(0)
  const focusNodeRef = useRef<Node | null>(null)

  const updateCaretPosition = () => {
    if (ref.current) {
      const { offset, focusNode } = getCaretIndex(ref.current)
      offsetRef.current = offset
      focusNodeRef.current = !ref.current.innerHTML ? null : focusNode || null
    }
  }

  const addContent = (content: string) => {
    if (!content || !ref.current) return

    const node = createElementFromHTML(content, true)
    new Set(node.querySelectorAll('[data-readonly]')).forEach((element) => {
      element.removeAttribute('data-readonly')
      element.setAttribute('contenteditable', 'false')
    })

    let focusNodeLocal = focusNodeRef.current
    let offsetLocal = offsetRef.current

    if (ref.current) {
      if (!focusNodeLocal) {
        ref.current.focus()
        const { focusNode, offset } = getCaretIndex(ref.current)

        focusNodeLocal = focusNode || ref.current || null
        offsetLocal = offset || 0
      }
      if (focusNodeLocal) {
        if (focusNodeLocal.nodeName === '#text') {
          if (focusNodeLocal.textContent) {
            const firstPart = focusNodeLocal.textContent.slice(0, offsetLocal)
            const firstNode = createElementFromHTML(firstPart, true)
            const secondPart = focusNodeLocal.textContent.slice(offsetLocal)
            const secondNode = createElementFromHTML(secondPart, true)
            const newNode = document.createElement('span')

            if (firstPart) {
              newNode.appendChild(firstNode)
            }
            newNode.appendChild(node)
            if (secondPart) {
              newNode.appendChild(secondNode)
            }
            if (newNode) {
              const parentNode = focusNodeLocal.parentNode
              if (parentNode) {
                parentNode.replaceChild(newNode, focusNodeLocal)
              }
            }
            if (ref.current) {
              setMessageText(ref.current.innerHTML, ref.current.innerText)
              try {
                if (secondPart) {
                  placeCaretAtStart(secondNode)
                } else {
                  placeCaretAtEnd(newNode)
                }
              } catch (e) {
                logger.error(e)
              }
            }
          } else {
            const parentNode = focusNodeLocal.parentNode
            const newNode = createElementFromHTML(content, true)

            if (parentNode) {
              parentNode.replaceChild(newNode, focusNodeLocal)
            }
            setMessageText(ref.current.innerHTML, ref.current.innerText)
            if (focusNodeLocal) {
              placeCaretAtEnd(focusNodeLocal)
            } else {
              placeCaretAtEnd(ref.current)
            }
          }
        } else {
          if (focusNodeLocal.firstChild?.nodeName === 'BR') {
            focusNodeLocal.removeChild(focusNodeLocal.firstChild)
          }
          focusNodeLocal.appendChild(node)
          setMessageText(ref.current.innerHTML, ref.current.innerText)
          if (focusNodeLocal) {
            placeCaretAtEnd(focusNodeLocal)
          } else {
            placeCaretAtEnd(ref.current)
          }
        }

        const { focusNode } = getCaretIndex(ref.current)

        const testFocusNodeLocal = focusNode || null
        if (!testFocusNodeLocal) {
          placeCaretAtEnd(ref.current)
        }
      }
    }
    updateCaretPosition()
    setUpdateTriggerContent()
  }

  const { onHideMergeFieldsTooltips, mergeFieldTooltipComponent } = useMergeFields({
    trigger: ref.current,
    updateTriggerDeps: [ref.current, updateTriggerContent, replaceTriggerContent],
    withEdit: true,
    setUpdateTriggerContent,
  })

  const handleChange = () => {
    if (ref.current) {
      if (ref.current.innerHTML === '<br>') {
        setMessageText('', '')
      } else {
        setMessageText(ref.current.innerHTML, ref.current.innerText)
      }
    }
  }

  useEffect(() => {
    setAddContentFn(addContent)
    return () => {
      setAddContentFn(null)
    }
  }, [])

  useEffect(() => {
    if (ref.current && !messageInnerText) {
      ref.current.innerHTML = ''
      updateCaretPosition()
    }
  }, [messageInnerText])

  useEffect(() => {
    if (ref.current && (replaceTriggerContent || messageHtml)) {
      ref.current.innerHTML = messageHtml
    }
  }, [replaceTriggerContent])

  useEffect(() => {
    if (ref.current && focusMessageFieldTrigger) {
      setActiveMessageField(true)
      placeCaretAtEnd(ref.current)
    }
  }, [focusMessageFieldTrigger, ref.current])

  const onPaste: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()

    const text = e.clipboardData.getData('text/plain')
    window.getSelection()?.getRangeAt(0)?.deleteContents()
    updateCaretPosition()

    addContent(
      textToHtml(entities, {
        text,
        extraSpace: true,
      })
    )
  }

  const onScroll = () => {
    onHideMergeFieldsTooltips()
  }

  useEffect(() => {
    if (updateTriggerContent) {
      handleChange()
    }
  }, [updateTriggerContent])

  const onFocus = () => {
    setActiveMessageField(true)
    updateCaretPosition()
    clearError()
  }

  const onBlur = () => {
    updateCaretPosition()
  }

  return (
    <>
      {mergeFieldTooltipComponent}
      <div
        ref={ref}
        className={classNames(
          styles.wrap,
          styles[`type${type}`],
          {
            [styles.withContent]: messageHtml,
            [styles.active]: activeMessageField,
          },
          className
        )}
        placeholder={'Write your message...'}
        contentEditable={true}
        onInput={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onPaste={onPaste}
        onScroll={onScroll}
      />
    </>
  )
})
